import { useEffect, useState } from "react";
import { WinShowcase } from "../components/WinShowcase";
import { WinsGallery } from "../components/WinsGallery";
import { Link, useNavigate } from "react-router-dom";
import useSWR from "swr";
import { toast } from "sonner";
import { ArrowLeft, Check, Crown, Loader2, LogOut } from "lucide-react";
import { api, formatApiError } from "../lib/api";
import { useAuth } from "../context/AuthContext";

const FEATURES = [
  "Tutte le partite reali di oltre 20 campionati, con risultati live",
  "Formazioni ufficiali e probabili, meteo, classifica, forma e scontri diretti",
  "Statistiche corner delle ultime 10 e statistiche stagionali di ogni giocatore",
  "Fino a 12 schedine AI ogni giorno sui campionati europei (quota 2, 5, 10, 30, quote alte, bomba 90, Serie A e B, bomba Over 60, corner Over 12,5 e marcatori), con storico delle vincite",
  "Pronostici AI 1X2, doppia chance e risultato esatto con % di fattibilità",
  "Over/Under, Goal/No Goal ed expected goals",
  "Pronostici corner: totali, per squadra e primo tempo",
  "Cartellini e giocatori: gol, assist e ammonizioni",
  "Pronostici consigliati e fattori chiave della partita",
  "Analisi aggiornata con formazioni ufficiali e partite live",
];

function openCheckout(url) {
  if (window.self === window.top) return window.location.assign(url);
  try {
    window.top.location.href = url;
  } catch {
    if (!window.open(url, "_blank", "noopener")) window.location.assign(url);
  }
}

const fmtDate = (d) => (d ? new Date(d).toLocaleDateString("it-IT", { day: "2-digit", month: "long", year: "numeric" }) : "");

const STATUS_LABEL = { trialing: "Prova gratuita", active: "Attivo", past_due: "Pagamento in sospeso", canceled: "Annullato", unpaid: "Non pagato", incomplete: "Incompleto" };

function ManageCard({ user, setUser }) {
  const [busy, setBusy] = useState(false);
  const act = async (path, msg) => {
    setBusy(true);
    try {
      const { data } = await api.post(path);
      setUser(data);
      toast.success(msg);
    } catch (e) {
      toast.error(formatApiError(e));
    } finally {
      setBusy(false);
    }
  };
  const end = user.subscription_status === "trialing" ? user.trial_end : user.current_period_end;
  return (
    <div className="surface-raised p-6 border-l-2 border-[#39FF14]" data-testid="subscription-manage">
      <div className="label text-[10px]">Il tuo abbonamento</div>
      <div className="heading text-3xl font-black mt-1" data-testid="subscription-status">{STATUS_LABEL[user.subscription_status] || (user.role === "admin" ? "Admin" : "—")}</div>
      {end && (
        <p className="text-sm text-[#A1A1AA] mt-2">
          {user.cancel_at_period_end ? `Termina il ${fmtDate(end)}` : user.subscription_status === "trialing" ? `Prova gratuita fino al ${fmtDate(end)}, poi 5,99 €/mese` : `Prossimo rinnovo: ${fmtDate(end)}`}
        </p>
      )}
      {user.subscription_status && (
        <button
          disabled={busy}
          onClick={() => (user.cancel_at_period_end ? act("/payments/resume", "Abbonamento riattivato") : act("/payments/cancel", "Il rinnovo automatico è stato disattivato"))}
          data-testid={user.cancel_at_period_end ? "resume-subscription-button" : "cancel-subscription-button"}
          className="mt-5 px-4 py-2 border border-[#27272A] text-xs font-bold uppercase tracking-wider hover:border-white transition-colors disabled:opacity-50"
        >
          {user.cancel_at_period_end ? "Riattiva rinnovo" : "Disattiva rinnovo"}
        </button>
      )}
    </div>
  );
}

export default function Premium() {
  const { user, setUser, logout } = useAuth();
  const navigate = useNavigate();
  const { data: plan } = useSWR("plan", () => api.get("/payments/plan").then((r) => r.data));
  const [loading, setLoading] = useState(false);
  const [checkoutUrl, setCheckoutUrl] = useState(null);

  useEffect(() => {
    const reset = () => setLoading(false);
    window.addEventListener("pageshow", reset);
    return () => window.removeEventListener("pageshow", reset);
  }, []);

  const subscribe = async () => {
    if (!user) return navigate("/register?next=/premium");
    setLoading(true);
    try {
      const { data } = await api.post("/payments/checkout", { origin_url: window.location.origin });
      setCheckoutUrl(data.checkout_url);
      openCheckout(data.checkout_url);
      setTimeout(() => setLoading(false), 4000);
    } catch (e) {
      toast.error(formatApiError(e));
      setLoading(false);
    }
  };

  const price = plan ? plan.amount.toLocaleString("it-IT", { style: "currency", currency: plan.currency.toUpperCase() }) : "5,99 €";
  const trial = user && user.trial_used ? null : plan?.trial_days || null;

  return (
    <div className="min-h-screen px-6 lg:px-10 py-10 max-w-6xl mx-auto">
      <div className="flex items-center justify-between gap-4">
        {user?.is_premium ? (
          <Link to="/" className="inline-flex items-center gap-2 text-sm text-[#A1A1AA] hover:text-white transition-colors" data-testid="premium-back-link">
            <ArrowLeft size={14} strokeWidth={1.5} /> Tutte le partite
          </Link>
        ) : (
          <div className="heading text-xl font-black">GOAL.AI</div>
        )}
        {user ? (
          <div className="flex items-center gap-3 text-sm text-[#A1A1AA] min-w-0">
            <span className="truncate" data-testid="premium-user-email">{user.email}</span>
            <button onClick={logout} data-testid="premium-logout-button" className="flex items-center gap-1.5 hover:text-[#FF3B30] transition-colors shrink-0">
              <LogOut size={14} /> Esci
            </button>
          </div>
        ) : (
          user === false && <Link to="/login?next=/premium" className="text-sm text-[#39FF14] font-bold" data-testid="premium-login-link">Accedi</Link>
        )}
      </div>
      {!user?.is_premium && <div className="mt-8 space-y-10"><WinShowcase /><WinsGallery limit={4} /></div>}
      <div className="mt-10 grid grid-cols-1 lg:grid-cols-[1.2fr_1fr] gap-10 items-start">
        <div className="fade-up order-2 lg:order-1">
          <div className="label flex items-center gap-2 text-[#39FF14]"><Crown size={12} /> GOAL.AI Premium</div>
          <h1 className="heading text-4xl sm:text-5xl lg:text-6xl font-black tracking-tighter mt-3 leading-[0.95]">
            Analisi AI complete<br />per ogni partita
          </h1>
          <p className="text-[#A1A1AA] mt-5 max-w-lg">GOAL.AI è un servizio riservato agli abbonati Premium: partite, statistiche, pronostici AI con la percentuale di fattibilità e schedine giornaliere.</p>
          {!user?.is_premium && (
            <Link to="/vincite" data-testid="premium-free-slips-link" className="mt-5 inline-flex items-center gap-2 border border-[#FFCC00]/60 text-[#FFCC00] px-4 py-2 text-xs font-black uppercase tracking-wider hover:bg-[#FFCC00] hover:text-black transition-colors">
              Guarda le vincite
            </Link>
          )}
          <ul className="mt-8 space-y-3">
            {FEATURES.map((f) => (
              <li key={f} className="flex gap-3 text-sm"><Check size={16} className="text-[#39FF14] shrink-0 mt-0.5" /> {f}</li>
            ))}
          </ul>
        </div>

        <div className="space-y-4 fade-up order-1 lg:order-2 lg:sticky lg:top-8" style={{ animationDelay: "120ms" }}>
          {user?.is_premium ? (
            <>
              <ManageCard user={user} setUser={setUser} />
              <Link to="/" data-testid="premium-go-app" className="block text-center bg-[#39FF14] text-black font-black uppercase tracking-wider text-sm py-3 hover:bg-white transition-colors">Vai alle partite</Link>
            </>
          ) : (
            <div className="surface-raised p-8 border-[#39FF14]/40" data-testid="premium-plan-card">
              {trial && <div className="inline-block bg-[#39FF14] text-black text-[11px] font-black uppercase tracking-wider px-2 py-1">{trial} giorni gratis</div>}
              <div className="mt-5 flex items-baseline gap-2">
                <span className="heading text-6xl font-black mono tracking-tighter" data-testid="premium-price">{price}</span>
                <span className="text-[#A1A1AA]">/ mese</span>
              </div>
              <p className="text-sm text-[#A1A1AA] mt-2">
                {trial ? `Gratis per la prima settimana, poi ${price} al mese. Disdici quando vuoi.` : `${price} al mese. Disdici quando vuoi.`}
              </p>
              <button
                onClick={subscribe}
                disabled={loading || user === null}
                data-testid="subscribe-button"
                className="mt-8 w-full bg-[#39FF14] text-black font-black uppercase tracking-wider text-sm py-4 hover:bg-white transition-colors disabled:opacity-60 flex items-center justify-center gap-2"
              >
                {loading && <Loader2 size={16} className="animate-spin" />}
                {user ? (trial ? "Inizia la prova gratuita" : "Abbonati ora") : "Registrati per iniziare"}
              </button>
              {checkoutUrl && !loading && (
                <a href={checkoutUrl} target="_top" rel="noopener" data-testid="open-checkout-link"
                  className="mt-3 block text-center text-xs text-[#39FF14] font-bold underline">
                  La pagina di pagamento non si è aperta? Tocca qui
                </a>
              )}
              <p className="text-[11px] text-[#71717A] mt-4 text-center">IVA inclusa · Pagamento sicuro con Stripe.</p>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
